<template>
  <v-container
    fluid
    class="daytime-advice-room pa-2"
  >
    <v-row
      class="pa-2"
      align-content="center"
    >
      <v-col
        cols="2"
        sm="2"
        md="2"
        :class="['pa-0 pl-4 consultation-list-col', { 'consultation-list-col--with-action-bar': hasActionBar }]"
      >
        <v-row
          no-gutters
          align-content="center"
          class="list-fixed-row"
        >
          <v-col>
            <template v-if="waiting()">
              <v-btn
                v-if="waiting()"
                color="pink"
                class="font-weight-bold ml-0 white--text"
                block
                light
                :disabled="isProcessing()"
                @click="startDaytimeAdvice()"
              >
                <v-icon
                  icon="mdi-message-text"
                />
                日中助産師 担当開始
              </v-btn>
            </template>
            <template v-else>
              <v-btn
                :color="isReady() && canFinishDaytimeAdvice() ? 'indigo' : 'grey'"
                class="font-weight-bold ml-0 white--text"
                light
                block
                :disabled="isProcessing() || !canFinishDaytimeAdvice()"
                @click="finishDaytimeAdvice()"
              >
                <v-icon
                  icon="mdi-message-bulleted-off"
                />
                日中助産師 担当終了
              </v-btn>
            </template>
          </v-col>
        </v-row>
        <v-row
          no-gutters
          align-content="center"
          class="list-fixed-row"
        >
          <v-col>
            <span style="font-size:12px;font-weight:bold;">相談一覧</span>
            <span style="font-size:12px;font-weight:bold; margin-left: 4px;">（現在の件数：{{ assignedSheetsCount }}件）</span>
          </v-col>
        </v-row>
        <div class="consultation-list-scroll-area">
          <daytime-advice-assigned-sheet-list
            :attention-sheets="attentionSheets"
            :non-attention-sheets="nonAttentionSheets"
            :active-sheet-id="$route.params.id"
          />
        </div>
        <div class="pa-3 list-fixed-row">
          <span
            class="text-pink text-4"
            style="font-weight: bold;"
          >
            現在のプーリング: {{ poolingCount }}件
          </span>
        </div>
      </v-col>
      <v-col
        cols="10"
        sm="10"
        md="10"
        class="pa-0"
      >
        <router-view class="daytime_advice_sheet" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import timestampMixin from "../mixins/timestamp.js";
import processingMixin from "../mixins/processing.js";
import DaytimeAdviceAssignedSheetList from "./daytime_advice/DaytimeAdviceAssignedSheetList.vue";
import {
  compareAssignedSheets,
  decorateAssignedSheet,
  isSheetFinishBlocked,
  toTimeMs
} from "../utils/daytimeAdviceAssignedSheet.js";

const ASSIGNED_SHEET_POLLING_INTERVAL = 60 * 1000;
const ASSIGNED_SHEET_LOCAL_REFRESH_INTERVAL = 15 * 1000;

export default {
  name: "DaytimeAdviceRoom",
  components: {
    DaytimeAdviceAssignedSheetList
  },
  mixins: [timestampMixin, processingMixin],
  data: () => ({
    messageChannel: null,
    noticeSheetIds: [],
    localRefreshTimeoutId: null,
    retryFetchTimeoutId: null,
    retryFetchPendingResolve: null,
    isUnmounted: false,
    serverTimeOffsetMs: 0,
    currentTimeMs: Date.now(),
  }),
  computed: {
    ...mapGetters([
      "currentDoctor",
      "assignedAdviceSheets",
      "assignedAdviceTimeoutId",
      "poolingCount"
    ]),
    sheetDecorationOptions: function() {
      return {
        currentTimeMs: this.currentTimeMs,
        serverTimeOffsetMs: this.serverTimeOffsetMs,
      };
    },
    decoratedAssignedAdviceSheets: function() {
      return this.assignedAdviceSheets
        .map(sheet => decorateAssignedSheet(sheet, this.sheetDecorationOptions))
        .sort(compareAssignedSheets);
    },
    attentionSheets: function() {
      return this.decoratedAssignedAdviceSheets.filter(sheet => sheet.groupKey == "attention");
    },
    nonAttentionSheets: function() {
      return this.decoratedAssignedAdviceSheets.filter(sheet => sheet.groupKey == "non_attention");
    },
    assignedSheetsCount: function() {
      return this.decoratedAssignedAdviceSheets ? this.decoratedAssignedAdviceSheets.length : 0;
    },
    hasActionBar: function() {
      return this.$route.name === "daytimeAdviceSheet";
    },
  },
  watch: {
    "$route" () {
      this.refresh();
      if(this.$route.params.id != null){
        this.noticeSheetIds = this.noticeSheetIds.filter(id => id !== parseInt(this.$route.params.id));
      }
    }
  },
  created() {
    this.isUnmounted = false;
    this.refresh(this.connectionNoticeChannel);
  },
  unmounted() {
    this.isUnmounted = true;
    // 自動更新を停止する
    clearInterval(this.assignedAdviceTimeoutId);
    clearInterval(this.localRefreshTimeoutId);
    clearTimeout(this.retryFetchTimeoutId);
    this.retryFetchTimeoutId = null;
    if(this.retryFetchPendingResolve != null){
      this.retryFetchPendingResolve();
      this.retryFetchPendingResolve = null;
    }
    this.disConnectionNoticeChannel();
  },
  methods: {
    startDaytimeAdvice: function(){
      this.startProcessing();
      this.$store.dispatch("daytimeAdviceStart").then(() => {
        this.refresh(this.endProcessing);
      });
    },
    finishDaytimeAdvice: function(){
      this.startProcessing();
      this.$store.dispatch("daytimeAdviceFinish").then(() => {
        this.refresh(this.endProcessing);
      }).catch(() => {
        this.endProcessing();
      });
    },
    refresh: function(afterAction){
      this.$store.dispatch("fetchCurrentDoctor").then(() => {
        this.startAutoReload();
        this.startLocalRefresh();
        if(afterAction != null){
          afterAction();
        }
      });
    },
    startAutoReload: function(){
      // 以前設定されたtimeoutがあればクリア
      clearInterval(this.assignedAdviceTimeoutId);
      // 最初の1回の実行
      this.fetchData();
      // 60秒
      var timeoutId = setInterval(() => this.fetchData(), ASSIGNED_SHEET_POLLING_INTERVAL);
      this.$store.dispatch("assignAssignedAdviceTimeoutId", timeoutId);
    },
    startLocalRefresh: function(){
      clearInterval(this.localRefreshTimeoutId);
      this.currentTimeMs = Date.now();
      this.localRefreshTimeoutId = setInterval(() => {
        this.currentTimeMs = Date.now();
      }, ASSIGNED_SHEET_LOCAL_REFRESH_INTERVAL);
    },
    fetchData: function(){
      return this.fetchAssignedAdviceSheetsWithRetry()
        .catch(() => {})
        .finally(() => {
          this.$store.dispatch("fetchPoolingCount");
        });
    },
    // ユーザーの手動リロードの手間をなるべく減らすため、シート一覧の失敗に取得した場合、2秒後に1度リトライする。
    fetchAssignedAdviceSheetsWithRetry: function(){
      return this.$store.dispatch("fetchAssignedAdviceSheets")
        .then(assignedAdviceSheets => {
          this.currentTimeMs = Date.now();
          // 並行リクエストで破棄されたレスポンス(undefined)は時刻オフセットの計算に使わない
          if(assignedAdviceSheets !== undefined){
            this.updateServerTimeOffset(assignedAdviceSheets);
          }
        })
        .catch(() => {
          if(this.isUnmounted){
            return Promise.resolve();
          }

          return new Promise((resolve, reject) => {
            this.retryFetchPendingResolve = resolve;
            this.retryFetchTimeoutId = setTimeout(() => {
              this.retryFetchTimeoutId = null;
              this.retryFetchPendingResolve = null;
              if(this.isUnmounted){
                resolve();
                return;
              }

              this.$store.dispatch("fetchAssignedAdviceSheets")
                .then(assignedAdviceSheets => {
                  this.currentTimeMs = Date.now();
                  if(assignedAdviceSheets !== undefined){
                    this.updateServerTimeOffset(assignedAdviceSheets);
                  }
                  resolve();
                })
                .catch(error => {
                  window.alert("一覧の更新に失敗しました。自動で再試行します。このエラーが頻発する場合は、一度画面を更新してください。");
                  reject(error);
                });
            }, 2 * 1000);
          });
        });
    },
    updateServerTimeOffset: function(assignedAdviceSheets){
      if(assignedAdviceSheets == null || assignedAdviceSheets.length == 0){
        this.serverTimeOffsetMs = 0;
        return;
      }

      const serverNow = assignedAdviceSheets[0].serverNow;
      if(serverNow == null) return;

      const serverNowMs = toTimeMs(serverNow);
      if(serverNowMs == null) return;

      this.serverTimeOffsetMs = serverNowMs - Date.now();
    },
    isReady: function(){
      return this.currentDoctor && this.currentDoctor.daytime_advice_active == true;
    },
    waiting: function(){
      return this.currentDoctor && this.currentDoctor.daytime_advice_active == false;
    },
    canFinishDaytimeAdvice: function(){
      // 要返信/返信済み/終了可能が存在する場合、担当終了ボタンを押下不可
      return this.decoratedAssignedAdviceSheets.find(sheet => isSheetFinishBlocked(sheet)) == null;
    },
    connectionNoticeChannel: function(){
      console.log(this.timestamp() + " connection Daytime Advice Notice Message Channel.");
      var that = this;
      this.disConnectionNoticeChannel();
      this.messageChannel = this.$cable.subscriptions.create(
        {
          channel: "DaytimeAdviceNoticeChannel",
        },
        {
          received: (data) =>  {
            console.log(that.timestamp() + " received Daytime Advice notice message.");
            var noticeInfo = JSON.parse(data["notice"]);
            // 通知対象の相談IDを保存する
            if(this.$route.params.id != noticeInfo.sheetId){
              if(this.noticeSheetIds.indexOf(noticeInfo.sheetId) == -1){
                this.noticeSheetIds.push(noticeInfo.sheetId);
              }
              // 最新データを取得し直す
              this.fetchData();
            }
          },
          connected: function() {
            console.log(that.timestamp() + " connected Daytime Advice notice message channel.");
          },
          disconnected: function() {
            console.log(that.timestamp() + " disconnected Daytime Advice notice message channel.");
          },
          rejected: function() {
            console.log(that.timestamp() + " rejected Daytime Advice notice message channel.");
          },
        }
      );
    },
    disConnectionNoticeChannel: function(){
      if(this.messageChannel == null) return;
      console.log(this.timestamp() + " disconnection Daytime Advice notice message Channel");
      this.messageChannel.unsubscribe();
    }
  }
};
</script>

<style lang="scss" scoped>
// 相談一覧部分だけをスクロール領域にし、ボタン・見出し・プーリング数は常に表示する。
.consultation-list-col {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 96px); /* 96px = compact TopMenu(48px) + v-tabs(48px) */
  overflow: hidden;
}

.consultation-list-col--with-action-bar {
  height: calc(100vh - 96px - 64px);
}

// ボタン・見出し・プーリング数は常に自然の高さで表示し、縮めない
.list-fixed-row {
  flex: 0 0 auto;
}

// 相談一覧のみを残り高さいっぱいにしてスクロールさせる
// (flex-basisを0にし、コンテンツ量に依存せず残り領域を充てる)
.consultation-list-scroll-area {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
}
</style>
